import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { get } from '../lib/api.ts'
import { IconClose, IconSearch } from './icons.tsx'
import { Spinner } from './ui.tsx'

export type Option = { id: string; label: string; sub?: string | null }

/**
 * Search-as-you-type picker backed by a lookup endpoint.
 *
 * It never loads the whole table: it asks the server for the best matches
 * for whatever has been typed, debounced, and cancels the previous request
 * so a slow earlier response cannot land after a newer one.
 */
export function Combobox({ endpoint, value, onChange, placeholder, disabled, autoFocus }: {
  endpoint: string
  value: Option | null
  onChange: (o: Option | null) => void
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
}) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<Option[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(0)
  const abort = useRef<AbortController>()
  const box = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  // The list is rendered into <body> rather than next to the input.
  // Inside the prescription's line table the input sits in a container
  // with overflow-x, which clips an absolutely positioned child -- the
  // dropdown appeared as a two-pixel sliver under the field. A portal
  // escapes every ancestor's overflow; the position is measured instead.
  const [rect, setRect] = useState<{ top: number; left: number; width: number; bottom: number } | null>(null)

  const measure = useCallback(() => {
    const el = box.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 8

    // A dropdown is only worth as much as the names it can show. Matching
    // the input's width works on a desktop, but inside the prescription
    // form the input sits in a narrow grid cell -- on a phone that made the
    // list about 130px wide, so "Diapha Capsule" wrapped onto two clipped
    // lines and the product was unpickable. The list may therefore be wider
    // than the field that opened it, clamped to the screen.
    const width = Math.min(Math.max(r.width, 260), window.innerWidth - margin * 2)

    // Widening it rightwards would run off the edge, so the left edge
    // slides back just far enough to fit.
    const left = Math.min(Math.max(margin, r.left), window.innerWidth - width - margin)

    // Below the field unless there is no room there, in which case above:
    // on a phone with the keyboard up, "below" can be entirely off-screen.
    const below = window.innerHeight - r.bottom
    const flip = below < 200 && r.top > below
    setRect({ top: flip ? -1 : r.bottom + 4, left, width, bottom: flip ? window.innerHeight - r.top + 4 : -1 })
  }, [])

  useLayoutEffect(() => {
    if (!open) { setRect(null); return }
    measure()
    // Capture phase, so scrolling any ancestor keeps the list attached.
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open, measure])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(async () => {
      abort.current?.abort()
      const ctrl = new AbortController()
      abort.current = ctrl
      setLoading(true)
      try {
        const params = new URLSearchParams({ limit: '10' })
        if (query.trim()) params.set('q', query.trim())
        const res = await get<{ items: Option[] }>(`${endpoint}?${params}`, ctrl.signal)
        setItems(res.items)
        setActive(0)
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setItems([])
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    }, query.trim() ? 180 : 0)
    return () => window.clearTimeout(t)
  }, [query, open, endpoint])

  // Close when focus or a click leaves the control entirely.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      // The list is no longer a descendant of the box, so it has to be
      // checked separately or clicking an option would close first.
      if (box.current?.contains(t) || listRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => () => abort.current?.abort(), [])

  function choose(o: Option) {
    onChange(o)
    setOpen(false)
    setQuery('')
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => Math.min(items.length - 1, i + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(0, i - 1)) }
    else if (e.key === 'Enter') {
      if (open && items[active]) { e.preventDefault(); choose(items[active]!) }
    } else if (e.key === 'Escape') { setOpen(false) }
  }

  if (value && !open) {
    return (
      <div className="combo-chosen">
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="t truncate">{value.label}</div>
          {value.sub ? <div className="s truncate">{value.sub}</div> : null}
        </div>
        {!disabled ? (
          <button type="button" className="icon-btn" aria-label="Change"
            onClick={() => { onChange(null); setOpen(true); setQuery('') }}>
            <IconClose size={14} />
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="combo" ref={box}>
      <div className="search" style={{ maxWidth: 'none' }}>
        <IconSearch className="ic" />
        <input
          className="input" style={{ paddingLeft: 32 }}
          value={query}
          placeholder={placeholder ?? 'Type to search'}
          disabled={disabled}
          autoFocus={autoFocus}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onKeyDown={onKey}
        />
        {loading ? (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <Spinner />
          </span>
        ) : null}
      </div>

      {open && rect ? createPortal(
        <div
          className="combo-list" id={listId} role="listbox" ref={listRef}
          style={rect.bottom >= 0
            ? { position: 'fixed', bottom: rect.bottom, left: rect.left, width: rect.width }
            : { position: 'fixed', top: rect.top, left: rect.left, width: rect.width }}
        >
          {items.length === 0 && !loading ? (
            <div className="combo-empty">
              {query.trim() ? 'No matches' : 'Start typing to search'}
            </div>
          ) : null}
          {items.map((o, i) => (
            <button
              key={o.id} type="button" className="combo-item" role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(o)}
            >
              <span className="t">{o.label}</span>
              {o.sub ? <span className="s">{o.sub}</span> : null}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </div>
  )
}
